import { createSlice } from "@reduxjs/toolkit";
import { organizationProfileApi } from "Services/OrganizationProfileService";

export const initialState = {
    message: {
        text: "",
        title: "",
        isOpen: false,
    },
    toaster: {
        message: "",
        severity: "error",
        isOpen: false,
        format: "",
        withTransition: true,
        adjustedCenter: false,
        anchorOrigin: null
    },
    countryList: [
        { key: "UnitedStates", value: "United States of America" },
        { key: "Canada", value: "Canada" },
    ],
    token: null,
    userToken: null,
    previewStagingEnabled: (typeof localStorage !== 'undefined' && localStorage.getItem('previewStagingEnabled') === 'true'),
    menuItem: {
        items: []
    },
    documentGenerator: {
        projectFundersVisibility: {
            inquiry: false,
            proposal: false,
            thankyou: false,
            impactReport: false,
            customLetter: false,
        }
    },
    subscriptionInfo: {
        subscriptionDate: null,
        expirationDate: null,
        daysLeft: null,
        showRenew: null,
        showInfo: null
    },
    openLoginDialog: false,
    chunkReloadFailed: false,
    drawerOpen: false,
};

const sharedSlice = createSlice({
    name: "shared",
    initialState,
    reducers: {
        toggleStagingPreview: (state) => {
            state.previewStagingEnabled = !state.previewStagingEnabled;
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('previewStagingEnabled', state.previewStagingEnabled ? 'true' : 'false');
            }
        },
        showSuccessToaster: (state, action) => {
            state.toaster.message = action.payload;
            state.toaster.isOpen = true;
            state.toaster.severity = "success";
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        showSuccessToasterBottomLeft: (state, action) => {
            state.toaster.message = action.payload;
            state.toaster.isOpen = true;
            state.toaster.severity = "success";
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = { vertical: "bottom", horizontal: "left" };
        },
        showSuccessToasterWithoutTransitions: (state, action) => {
            state.toaster.message = action.payload;
            state.toaster.isOpen = true;
            state.toaster.severity = "success";
            state.toaster.format = "";
            state.toaster.withTransition = false;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        showSuccessWithFormat: (state, action) => {
            state.toaster.message = action.payload.message;
            state.toaster.isOpen = true;
            state.toaster.severity = "success";
            state.toaster.format = action.payload.format;
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
         showSuccessWithCenterAdjustment: (state, action) => {
            state.toaster.message = action.payload;
            state.toaster.isOpen = true;
            state.toaster.severity = "success";
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = true;
            state.toaster.anchorOrigin = null;
        },
        showWarningToaster: (state, action) => {
            state.toaster.message = action.payload.message;
            state.toaster.isOpen = true;
            state.toaster.severity = "warning";
            state.toaster.format = action.payload.format ?? "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        showErrorToaster: (state, action) => {
            state.toaster.message = action.payload;
            state.toaster.isOpen = true;
            state.toaster.severity = "error";
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        showErrorToasterWithFormat: (state, action) => {
            state.toaster.message = action.payload.message;
            state.toaster.isOpen = true;
            state.toaster.severity = "error";
            state.toaster.format = action.payload.format ?? "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        closeToaster: (state, action) => {
            state.toaster.message = "";
            state.toaster.isOpen = false;
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        showMessage: (state, action) => {
            state.message.text = action.payload;
            state.message.title = "Error";
            state.message.isOpen = true;
        },
        showSessionTimeout: (state, action) => {
            state.toaster.message = 'Session Timeout';
            state.toaster.isOpen = true;
            state.toaster.severity = "error";
            state.toaster.format = "";
            state.toaster.withTransition = true;
            state.toaster.adjustedCenter = false;
            state.toaster.anchorOrigin = null;
        },
        closeMessage: (state, action) => {
            state.message.message = "";
            state.message.isOpen = false;
        },
        setUserToken: (state, action) => {
            state.userToken = action.payload;
        },
        loadMenuItems: (state, action) => {
            state.menuItem = { ...action.payload };
        },
        collapseMenu: (state, action) => {
            const itemsCopy = [...state.menuItem.items].map(copy => ({
                ...copy,
                collapse: (copy.id === action.payload) ? !copy.collapse : false
            }));
            state.menuItem.items = itemsCopy;
        },
        collapseAll: (state, action) => {
            const itemsCopy = [...state.menuItem.items].map(copy => ({
                ...copy,
                collapse: false
            }));
            state.menuItem.items = itemsCopy;
        },
        setProjectFundersVisibility: (state, action) => {
            const { key, value } = action.payload;
            state.documentGenerator.projectFundersVisibility[key] = value;
        },
        setLoginDialogOpen: (state, action) => {
            state.openLoginDialog = action.payload;
        },
        setChunkReloadFailed: (state, action) => {
            state.chunkReloadFailed = action.payload;
        },
        setDrawerOpen: (state, action) => {
            state.drawerOpen = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            organizationProfileApi.endpoints.getSubscriptionInfo.matchFulfilled, (state, action) => {
                state.subscriptionInfo.expirationDate = action.payload.expirationDate;
                state.subscriptionInfo.subscriptionDate = action.payload.subscriptionDate;
                state.subscriptionInfo.daysLeft = action.payload.daysLeft;
                state.subscriptionInfo.showRenew = action.payload.showRenew;
                state.subscriptionInfo.showInfo = action.payload.showInfo;
            }
        )
    }
});

export const {
    toggleStagingPreview,
    showSuccessToaster,
    showSuccessToasterBottomLeft,
    showErrorToaster,
    showErrorToasterWithFormat,
    closeToaster,
    showMessage,
    closeMessage,
    setUserToken,
    showSuccessWithFormat,
    showWarningToaster,
    loadMenuItems,
    collapseMenu,
    collapseAll,
    showSessionTimeout,
    setProjectFundersVisibility,
    showSuccessToasterWithoutTransitions,
    setLoginDialogOpen,
    setChunkReloadFailed,
    showSuccessWithCenterAdjustment,
    setDrawerOpen,
} = sharedSlice.actions;

export default sharedSlice.reducer;
